import type {
  BaseQueryFn,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { type AxiosError, type AxiosRequestConfig } from 'axios'
import { retry, createApi } from '@reduxjs/toolkit/query/react'
import {
  type ServiceCursorPaginatedResponseType,
  ServiceErrorHandler,
  type ServicePaginatedResponseType,
} from '@/connect-types/backend/service'
import { customAxios } from '@/utils/axiosHelper'
import config from '@/config'
import { buildUrl } from '@/utils/common'
import { shouldBailout } from '../helpers'
import {
  type DesignMyNightIntegration,
  type SauceIntegration,
  type SauceLocationsResponse,
  type SauceOrdersResponse,
  type SauceRunsResponse,
  type RunStatus,
  type OpenTableConfig,
  type OpenTableConfigResponse,
  type OpenTableUpdateRequest,
  type OpenTableTestCredentialsRequest,
  type OpenTableTestCredentialsResponse,
  type OpenTableBookingsResponse,
  type CreateLoadTestRequest,
  type LoadTestListResponse,
  type LoadTestResponse,
  type LoadTestStatus,
  type CreateLiveBrowserSessionRequest,
  type CreateLiveBrowserSessionResponse,
  type CloseLiveBrowserSessionResponse,
  type FetchResDiaryConfigResponse,
  type FetchResDiaryBookingsRequest,
  type FetchResDiaryBookingsResponse,
} from './types'

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<{
    url: string
    method: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
  }> =>
  async ({ url, method, data, params }) => {
    try {
      let axiosUrl = baseUrl + url
      if (url.startsWith('https://')) {
        axiosUrl = url
      }

      const result = await customAxios({
        url: axiosUrl,
        method,
        data,
        params,
      })

      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError
      console.log(err, 'error-here')

      if (
        method !== 'get' &&
        err.response?.status !== 401 &&
        err.response?.status !== 404 &&
        !url.includes('segment') &&
        !url.includes('oauth')
      ) {
        ServiceErrorHandler(err)
      }

      if (shouldBailout(err)) {
        retry.fail(err)
      }

      //
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      }
    }
  }

const agentSmithApi = createApi({
  reducerPath: 'agent-smith',
  baseQuery: retry(axiosBaseQuery({ baseUrl: config.url.agent_smith }), {
    maxRetries: 2,
  }),
  tagTypes: ['DesignMyNight', 'Sauce', 'OpenTable', 'LoadTest'],
  endpoints: (build) => ({
    getIntegrationSettings: build.query<
      DesignMyNightIntegration,
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/${orgId}/design-my-night`,
        method: 'get',
      }),
      providesTags: (result, _error, { orgId }) => [
        { type: 'DesignMyNight', id: orgId },
      ],
    }),
    upsertIntegrationSettings: build.mutation<
      DesignMyNightIntegration,
      { orgId: string; data: DesignMyNightIntegration }
    >({
      query: ({ orgId, data }) => ({
        url: `/${orgId}/design-my-night`,
        method: 'put',
        data,
      }),
      invalidatesTags: (results: any) => [
        { type: 'DesignMyNight', id: results?.org_id },
      ],
    }),
    getSauceIntegrationSettings: build.query<
      SauceIntegration,
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/${orgId}/sauce`,
        method: 'get',
      }),
      providesTags: (result, _error, { orgId }) => [
        { type: 'Sauce', id: orgId },
      ],
    }),
    upsertSauceIntegrationSettings: build.mutation<
      SauceIntegration,
      { orgId: string; data: SauceIntegration }
    >({
      query: ({ orgId, data }) => ({
        url: `/${orgId}/sauce`,
        method: 'put',
        data,
      }),
      invalidatesTags: (results: any) => [
        { type: 'Sauce', id: results?.org_id },
      ],
    }),
    getSauceLocations: build.query<SauceLocationsResponse, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/${orgId}/sauce/locations`,
        method: 'get',
      }),
    }),
    getSauceOrders: build.query<
      SauceOrdersResponse,
      { orgId: string; sauceVenueId: string; page?: number; limit?: number }
    >({
      query: ({ orgId, sauceVenueId, page = 1, limit = 15 }) => ({
        url: `/${orgId}/sauce/orders`,
        method: 'get',
        params: {
          sauce_venue_id: sauceVenueId,
          page,
          limit,
        },
      }),
    }),
    getSauceRuns: build.query<
      SauceRunsResponse,
      { orgId: string; cursor?: string; limit?: number; status?: RunStatus }
    >({
      query: ({ orgId, cursor, limit = 20, status }) => ({
        url: `/${orgId}/sauce/runs`,
        method: 'get',
        params: {
          ...(cursor && { cursor }),
          limit,
          ...(status && { status }),
        },
      }),
    }),
    // OpenTable endpoints
    getOpenTableConfig: build.query<OpenTableConfigResponse, { orgId: string }>(
      {
        query: ({ orgId }) => ({
          url: `/${orgId}/open-table`,
          method: 'get',
        }),
        providesTags: (result, _error, { orgId }) => [
          { type: 'OpenTable', id: orgId },
        ],
      }
    ),
    updateOpenTableConfig: build.mutation<
      OpenTableConfigResponse,
      { orgId: string; data: OpenTableUpdateRequest }
    >({
      query: ({ orgId, data }) => ({
        url: `/${orgId}/open-table`,
        method: 'put',
        data,
      }),
      invalidatesTags: (result, _error, { orgId }) => [
        { type: 'OpenTable', id: orgId },
      ],
    }),
    testOpenTableCredentials: build.mutation<
      OpenTableTestCredentialsResponse,
      { orgId: string; data: OpenTableTestCredentialsRequest }
    >({
      query: ({ orgId, data }) => ({
        url: `/${orgId}/open-table/test_credentials`,
        method: 'post',
        data,
      }),
    }),
    getOpenTableBookings: build.query<
      OpenTableBookingsResponse,
      { orgId: string; venue_id: string; start?: string; end?: string }
    >({
      query: ({ orgId, venue_id, start, end }) => ({
        url: `/${orgId}/open-table/bookings`,
        method: 'get',
        params: {
          venue_id,
          ...(start && { start }),
          ...(end && { end }),
        },
      }),
    }),
    getOpenTableRuns: build.query<
      SauceRunsResponse,
      { orgId: string; cursor?: string; limit?: number; status?: RunStatus }
    >({
      query: ({ orgId, cursor, limit = 20, status }) => ({
        url: `/${orgId}/open-table/runs`,
        method: 'get',
        params: {
          ...(cursor && { cursor }),
          limit,
          ...(status && { status }),
        },
      }),
    }),

    // Load Test endpoints
    createLoadTest: build.mutation<LoadTestResponse, CreateLoadTestRequest>({
      query: (data) => ({
        url: '/load-test',
        method: 'post',
        data,
      }),
      invalidatesTags: ['LoadTest'],
    }),
    getLoadTests: build.query<
      LoadTestListResponse,
      {
        org_id?: string
        serial?: string
        status?: LoadTestStatus
        cursor?: string
        limit?: number
      }
    >({
      query: ({ org_id, serial, status, cursor, limit = 20 }) => ({
        url: '/load-test',
        method: 'get',
        params: {
          ...(org_id && { org_id }),
          ...(serial && { serial }),
          ...(status && { status }),
          ...(cursor && { cursor }),
          limit,
        },
      }),
      providesTags: ['LoadTest'],
    }),
    getLoadTest: build.query<LoadTestResponse, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `/load-test/${jobId}`,
        method: 'get',
      }),
      providesTags: (result, _error, { jobId }) => [
        { type: 'LoadTest', id: jobId },
      ],
    }),
    cancelLoadTest: build.mutation<LoadTestResponse, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `/load-test/${jobId}/cancel`,
        method: 'post',
      }),
      invalidatesTags: ['LoadTest'],
    }),

    // Live Browser endpoints — interactive headless-browser session used for
    // the competitor-platform login handoff (see live-migration/).
    createLiveBrowserSession: build.mutation<
      CreateLiveBrowserSessionResponse,
      { orgId: string; data: CreateLiveBrowserSessionRequest }
    >({
      query: ({ orgId, data }) => ({
        url: `/${orgId}/live-browser/sessions`,
        method: 'post',
        data,
      }),
    }),
    closeLiveBrowserSession: build.mutation<
      CloseLiveBrowserSessionResponse,
      { orgId: string; sessionId: string }
    >({
      query: ({ orgId, sessionId }) => ({
        url: `/${orgId}/live-browser/sessions/${sessionId}/close`,
        method: 'post',
      }),
    }),
    // ResDiary-specific deterministic pull (docs/providers/resdiary.md) —
    // must run before closeLiveBrowserSession, needs the session still open.
    fetchResDiaryConfig: build.mutation<
      FetchResDiaryConfigResponse,
      { orgId: string; sessionId: string }
    >({
      query: ({ orgId, sessionId }) => ({
        url: `/${orgId}/live-browser/sessions/${sessionId}/resdiary-config`,
        method: 'post',
      }),
    }),
    // Runs after fetchResDiaryConfig, still on the same open session - see
    // that mutation's comment.
    fetchResDiaryBookings: build.mutation<
      FetchResDiaryBookingsResponse,
      { orgId: string; sessionId: string; data: FetchResDiaryBookingsRequest }
    >({
      query: ({ orgId, sessionId, data }) => ({
        url: `/${orgId}/live-browser/sessions/${sessionId}/resdiary-bookings`,
        method: 'post',
        data,
      }),
    }),
  }),
})

export default agentSmithApi
